import React from 'react';
import { StatsigProvider } from '@statsig/react-bindings';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { statsigConfig, getDeviceInfo } from '../config/statsig';
import { mapSupabaseUserToStatsig } from '../utils/statsigUser';
import { getSSRStatsigData } from '../utils/statsigSSRStore';
import { createLogger } from '../utils/logger';
import { initStatsigSentryIntegration } from '../utils/sentryStatsigIntegration';

const logger = createLogger({ module: 'StatsigWrapper', level: 'info' });

interface StatsigWrapperProps {
  children: React.ReactNode;
}

// Define which routes are protected and should always have an authenticated user
const PROTECTED_ROUTES = [
  '/home',
  '/profile',
  '/settings',
  '/files',
  '/resumes',
  '/job-search',
  '/timeline',
  '/retrospectives',
  '/admin'
];

// Check if current route is protected
const isProtectedRoute = (pathname: string): boolean => {
  return PROTECTED_ROUTES.some(route => pathname.startsWith(route));
};

export const StatsigWrapper: React.FC<StatsigWrapperProps> = ({ 
  children
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // Get SSR feature flags from global store
  const { initialFeatureFlags } = React.useMemo(() => {
    return getSSRStatsigData();
  }, []);
  
  // Get device info for user mapping
  const deviceInfo = React.useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    return getDeviceInfo();
  }, []);

  // Map Supabase user to Statsig user format
  const statsigUser = React.useMemo(() => {
    return mapSupabaseUserToStatsig(user, undefined, deviceInfo);
  }, [user, deviceInfo]);

  // Debug logging when user changes
  React.useEffect(() => {
    const isProtected = isProtectedRoute(location.pathname);
    logger.debug('User state changed', {
      hasUser: !!user,
      statsigUserID: statsigUser.userID,
      isAnonymous: statsigUser.userID === 'anonymous',
      userEmail: user?.email?.substring(0, 3) + '***' || 'none',
      isProtectedRoute: isProtected,
      authLoading: loading
    });
  }, [user, loading, statsigUser.userID, location.pathname]);

  // Don't render Statsig provider if no client key is available
  if (!statsigConfig.clientKey) {
    logger.warn('Statsig client key not found. Feature flags will not work.');
    return <>{children}</>;
  }

  // PERFORMANCE FIX: Always initialize immediately, never wait for auth
  // This allows the app to render while Statsig initializes in the background
  logger.debug('Initializing Statsig immediately for optimal performance', {
    userID: statsigUser.userID,
    isAnonymous: statsigUser.userID === 'anonymous',
    route: location.pathname,
    isProtectedRoute: isProtectedRoute(location.pathname)
  });

  // Initialize Sentry integration after Statsig is ready
  React.useEffect(() => {
    // Small delay to ensure Statsig is initialized
    const timer = setTimeout(() => {
      try {
        initStatsigSentryIntegration();
        logger.debug('Statsig-Sentry integration initialized');
      } catch (error) {
        logger.error('Failed to initialize Statsig-Sentry integration:', error);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <StatsigProvider
      sdkKey={statsigConfig.clientKey}
      user={statsigUser}
      options={{
        ...statsigConfig.options,
        // Ensure proper environment detection
        environment: {
          tier: import.meta.env.MODE
        },
        // PERFORMANCE: Enable local storage caching for faster subsequent loads
        disableStorage: false
      }}
      // PERFORMANCE FIX: Remove loadingComponent to make it completely non-blocking
      // The app will render immediately and Statsig will initialize in the background
    >
      {children}
    </StatsigProvider>
  );
}; 